import { tLog } from '../../lib/i18n.js'

// The headline "how many did my search find" line, above the card grid (owner 2026-07-31:
// "when results are shown where do i see how many are searched results?"). Until now the
// only place this number surfaced was the sidebar Category rail, sitting directly under
// `ყველა ტიპი {n}` — which is a tally of LOADED rows, not a corpus count. Two different
// corpora printed as two adjacent numbers is why the real total didn't read as one.
//
// Costs nothing: `total` already rides in on the page-0 response (core.js normalizes it to
// `{ total, rowsReturned, countUnit, unknownExcluded }`). This component must never trigger
// a fetch of its own — the second-count-costs-a-whole-extra-cluster-search lesson from
// IntakeTab's leadPrefetchOther applies here too.
//
// ⚠️ THREE rules, each one the difference between a count and a lie with digits in it:
//   1. UNIT — the server's count_unit is 'properties', i.e. CLUSTERS, not ads. The label
//      always names the unit; never render a bare number.
//   2. THE SHELF IS NOT INCLUDED — `unknown_excluded` (the possible-matches tier) is
//      counted separately and rendered by PossibleMatchesShelf's own header. Passing it as
//      `possible` shows it as a distinct "+n", never folded into the headline.
//   3. NULL UNTIL FETCHED — render nothing rather than the previous query's number. This
//      is leadsCachedTotal's own null-when-not-fetched rule ("a count from a different
//      filter set is a lie with digits in it", stated twice in LeadsFeed.jsx); a headline
//      is the most-read place in the feed, so it is the worst place to break it.
// `inline` (2026-08-02): the property feed now renders this INSIDE a shared line with the
// filter readout it used to sit above, so it must not open its own block element there. Leads
// still call it standalone.
const Wrap = ({ className, children }) => className ? <p className={className}>{children}</p> : <span>{children}</span>
// The same count as a STRING, for callers that cannot render a component — a `title`
// attribute, an aria-label. ONE definition, so the histogram bar tooltips in RangeFilters
// cannot drift from this component's wording or from rule 1: the unit is always named.
// `matching` (2026-08-27, live usability test): the SAME number answers two different
// questions and the agent could not tell which. Browsing, it is "how many properties are in
// scope"; with a search committed it is "how many MATCH" — the question the agent actually
// asked. Measured on the real Desk: a committed search for 2-bed/Saburtalo/pets/balcony sent
// every filter to the server and got total=59, but the headline still read "59 objects",
// indistinguishable in wording from the 3,563 shown a moment earlier for no search at all.
// The word now carries the difference; the number was already right.
// ⚠️ It is threaded through the STRING form, not just the component: the two were separate
// commits on separate branches, and a caller reading the text (a tooltip, an aria-label)
// must not get the browsing wording for a matched set.
export function resultCountText(total, unit = 'properties', matching = false) {
  const key = unit === 'leads' ? 'desk_result_count_leads'
    : matching ? 'desk_result_count_matching' : 'desk_result_count_properties'
  return tLog(key, { n: Number(total).toLocaleString('en-US') })
}

export function ResultCount({ total, unit = 'properties', possible = null, inline = false, matching = false }) {
  if (total == null) return null            // rule 3 — not fetched yet, say nothing
  return (
    <Wrap className={inline ? undefined : "mb-2 text-xs text-muted-foreground"}>
      {resultCountText(total, unit, matching)}
      {/* A literal space, not just the margin: ml-1.5 renders the gap but textContent
          concatenates to "ლიდი+1 შესაძლო", so anything reading the text rather than the
          pixels (copy-paste, screen reader) loses the word break. */}
      {possible > 0 && (<>
        {' '}
        <span className="ml-1.5 opacity-70">
          {tLog('desk_result_count_possible', { n: possible.toLocaleString('en-US') })}
        </span>
      </>)}
    </Wrap>
  )
}
